//This component allows for searching for certificate holders
import { Component, OnInit, NgZone, ChangeDetectorRef } from "@angular/core";
import { Router, ActivatedRoute, ParamMap } from "@angular/router";
import { StreamCertificateService } from "../../../services/stream-certificate.service";
import { SortService } from "../../../services/sort.service";
import { LoginStateService } from "../../../services/login-state.service";
import { CertificateHolder } from "src/app/classes/certificateHolder";
import { StringService } from "../../../services/string.service";
import { SearchService } from "src/app/services/search.service";
import { HttpParams } from "@angular/common/http";
import { combineLatest, Subscription } from "rxjs";

@Component({
  selector: "app-search",
  standalone: true,
  imports: [],
  templateUrl: "./search.component.html",
  styleUrls: ["./search.component.scss"],
})
export class SearchComponent implements OnInit {
  private query: string = null;
  private currentRouteKey: string = null;
  public isLoading: boolean = false;
  public people: Array<CertificateHolder> = [];
  public loggedIn: boolean = false;
  private sortAsc: boolean = true;
  private routeStateSubscription: Subscription;
  private searchSubscription: Subscription;
  private loginStateSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private streamService: StreamCertificateService,
    private loginStateService: LoginStateService,
    public strings: StringService,
    private searchService: SearchService,
  ) {
    if (loginStateService.loggedIn !== null) {
      this.loggedIn = loginStateService.loggedIn;
    }
  }

  ngOnInit() {
    //Watch for string changes to update translated labels
    this.strings.onArtesChange.subscribe(() => {
      this.cdr.detectChanges();
    });

    //Watch only route params/query params to avoid duplicate global navigation triggers
    this.routeStateSubscription = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap,
    ]).subscribe(([paramMap, queryParamMap]) => {
      this.loadSearchFromRoute(paramMap, queryParamMap);
    });

    //Watch for changes in login state
    this.loginStateSubscription =
      this.loginStateService.loggedInChange.subscribe((state: boolean) => {
        if (this.loggedIn !== state) {
          this.loggedIn = state;
        }
      });
  }

  //Unsubscribe on route change
  ngOnDestroy() {
    if (this.routeStateSubscription) {
      this.routeStateSubscription.unsubscribe();
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.loginStateSubscription) {
      this.loginStateSubscription.unsubscribe();
    }
  }

  private loadSearchFromRoute(
    paramMap: ParamMap,
    queryParamMap: ParamMap,
  ): void {
    const routeQuery = paramMap.get("query");
    const hasSkillFilters = queryParamMap.keys.length > 0;

    if (routeQuery === "skills" || hasSkillFilters) {
      const serializedParams = queryParamMap.keys
        .slice()
        .sort()
        .map((key) => `${key}=${queryParamMap.get(key) || ""}`)
        .join("&");
      const routeKey = `skills?${serializedParams}`;
      if (this.currentRouteKey === routeKey) {
        return;
      }
      this.currentRouteKey = routeKey;

      let httpParams = new HttpParams();
      for (let key of queryParamMap.keys) {
        const value = queryParamMap.get(key);
        if (value !== null) {
          httpParams = httpParams.set(key, value);
        }
      }

      if (httpParams.keys().length > 0) {
        this.searchSkills(httpParams);
      } else {
        this.people = [];
        this.isLoading = false;
      }
      return;
    }

    this.query = routeQuery;
    if (this.query !== null && this.query !== "" && this.query !== "skills") {
      const routeKey = `query:${this.query}`;
      if (this.currentRouteKey === routeKey) {
        return;
      }
      this.currentRouteKey = routeKey;
      this.search(encodeURIComponent(this.query));
    } else {
      this.currentRouteKey = null;
      this.people = [];
      this.isLoading = false;
    }
  }

  //Enable loading gif and send search request
  private search(query: string): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    this.setLoading(true);
    this.searchSubscription = this.searchService.search(query).subscribe({
      next: (response: any) => {
        try {
          this.ngZone.run(() => {
            this.setPeopleFromResponse(response);
          });
        } finally {
          this.setLoading(false);
        }
      },
      error: () => {
        this.setLoading(false);
      },
    });
  }

  private searchSkills(params: HttpParams) {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    this.setLoading(true);
    this.searchSubscription = this.searchService
      .searchSkills(params)
      .subscribe({
        next: (response: any) => {
          try {
            this.ngZone.run(() => {
              this.setPeopleFromResponse(response);
            });
          } finally {
            this.setLoading(false);
          }
        },
        error: () => {
          this.setLoading(false);
        },
      });
  }

  private setPeopleFromResponse(response: any): void {
    const people: Array<CertificateHolder> = [];
    if (response && response.data && response.data instanceof Array) {
      response.data.forEach((element) => {
        try {
          people.push(new CertificateHolder(element));
        } catch {
          //Skip malformed result entries to keep UI responsive
        }
      });
    }
    this.people = people;
    this.cdr.detectChanges();
  }

  private setLoading(value: boolean): void {
    this.ngZone.run(() => {
      this.isLoading = value;
      this.cdr.detectChanges();
    });
  }

  //Get newest certificate from certificate holder and display thorugh stream certificate component
  public streamCertificate(person: CertificateHolder) {
    let actionId = person.newest_issue_action_id;
    if (actionId) {
      this.streamService.toggleLoading(true);
      this.streamService
        .streamCertificate(
          person.id,
          person.newest_certificate.id,
          person.newest_issue_action_id,
        )
        .subscribe(
          (data) => {
            if (data && data["data"]) {
              this.streamService.toggle(data["data"]);
            }
            this.streamService.toggleLoading(false);
          },
          () => {
            this.streamService.toggleLoading(false);
          },
        );
    } else {
      alert(this.strings.artesStrings["NO_ACTIVE_CERTIFICATE"]);
    }
  }

  //Sort search results
  public sortTable(key: string, key2?: string) {
    this.sortAsc = !this.sortAsc;
    let sorter: SortService = new SortService(this.people);
    this.people = sorter.sort(key, this.sortAsc, key2);
  }

  //Open certificate holder page
  public openPerson(person: any) {
    this.router.navigate([]).then((result) => {
      window.open("user/" + person.id + "/data", "_blank");
    });
  }
}
